import React from "react";

import { Switch, SwitchClassKey, SwitchProps } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import withStyles from "@mui/styles/withStyles";
import { colors } from "@zapalacorp/tokens";

import { getBrandCustom } from "../../../util/getCustomColor";
import { AppFormControlLabel } from "../AppFormControlLabel";

interface Styles extends Partial<Record<SwitchClassKey, string>> {
  focusVisible?: string;
  thumbFilled: any;
  thumbOutlined: any;
  checkedFilled: any;
  checkedOutlined: any;
  switchBaseFilled: any;
  switchBaseOulined: any;
  trackFilled: any;
  trackOutlined: any;
  thumbDefault: any;
  checkedDefault: any;
  switchBaseDefault: any;
  inputDefault: any;
  trackDefault: any;
}

interface Props extends SwitchProps {
  classes: Styles;
  variant?: "default" | "filled" | "outlined";
  label?: React.ReactNode;
  labelPlacement?: "end" | "start" | "top" | "bottom";
}

const AppSwithComponent = ({
  classes,
  checked,
  label,
  labelPlacement,
  variant = "default",
  ...props
}: Props) => {
  const variantDictionary = {
    default: {
      root: classes.root,
      input: classes.inputDefault,
      switchBase: classes.switchBaseDefault,
      thumb: classes.thumbDefault,
      track: classes.trackDefault,
      checked: classes.checkedDefault,
    },
    outlined: {
      root: classes.root,
      switchBase: classes.switchBaseOulined,
      thumb: classes.thumbOutlined,
      track: classes.trackOutlined,
      checked: classes.checkedOutlined,
    },
    filled: {
      root: classes.root,
      switchBase: classes.switchBaseFilled,
      thumb: classes.thumbFilled,
      track: classes.trackFilled,
      checked: classes.checkedFilled,
    },
  };

  if (!label) {
    return (
      <Switch
        checked={checked}
        focusVisibleClassName={classes.focusVisible}
        disableRipple
        color="default"
        classes={variantDictionary[variant]}
        {...props}
      />
    );
  }

  return (
    <AppFormControlLabel
      label={label}
      labelPlacement={labelPlacement}
      control={
        <Switch
          checked={checked}
          focusVisibleClassName={classes.focusVisible}
          disableRipple
          color="default"
          classes={variantDictionary[variant]}
          {...props}
        />
      }
    />
  );
};

const AppSwithClasses = () =>
  createStyles({
    root: {
      width: 52,
      height: 32,
      padding: 1,
    },
    switchBaseDefault: {
      position: "relative",
      width: "100% !important",
      height: "100% !important",
      color: `${colors.gray500} !important`,
      padding: "0px !important",
      justifyContent: "flex-start",

      "&$focusVisible $thumbDefault": {
        color: getBrandCustom("primaryColor", colors.brandBlue500),
        border: `6px solid ${colors.white}`,
      },
      "&$checkedDefault": {
        width: "100%",
        height: "100%",
        transform: "translateX(0px) !important",

        "& $thumbDefault": {
          transform: "translateX(22px) !important",

          width: 24,
          height: 24,
          backgroundColor: colors.brandBlue50,
        },
        "& + $trackDefault": {
          opacity: 1,
          border: "none !important",
          backgroundColor: getBrandCustom("primaryColor", colors.brandBlue500),
        },
      },
    },
    inputDefault: {
      width: "100% !important",
      left: "0 !important",
    },
    thumbDefault: {
      position: "absolute",
      transform: "translateX(4px)",
      width: 16,
      height: 16,
      transition: "height 150ms, width 150ms, color 150ms, transform 150ms",
      willChange: "height, width, color",
    },
    trackDefault: {
      position: "absolute",
      left: 0,
      top: 0,
      borderRadius: "100px",
      border: `2px solid ${colors.gray500}`,
      backgroundColor: colors.gray100,
      opacity: 1,
    },
    checkedDefault: {},
    switchBaseFilled: {
      width: "fit-content",
      padding: 4,
      "&$checkedFilled": {
        transform: "translateX(20px)",
        color: colors.white,
        "& + $trackFilled": {
          backgroundColor: getBrandCustom("primaryColor", colors.brandBlue500),
          opacity: 1,
          border: "none",
        },
      },
      "&$focusVisible $thumbFilled": {
        color: getBrandCustom("primaryColor", colors.brandBlue500),
        border: `6px solid ${colors.white}`,
      },
    },
    thumbFilled: {
      width: 24,
      height: 24,
    },
    trackFilled: {
      borderRadius: 26 / 2,
      border: 0,
      backgroundColor: "#0055A41F",
      opacity: 1,
    },
    checkedFilled: {},
    switchBaseOulined: {
      width: "fit-content",
      padding: 8,
      color: `${colors.gray500} !important`,

      "&$focusVisible $thumbOutlined": {
        color: getBrandCustom("primaryColor", colors.brandBlue500),
        border: `6px solid ${colors.white}`,
      },
      "&$checkedOutlined": {
        transform: "translateX(20px)",
        "& + $trackOutlined": {
          opacity: 1,
        },
      },
    },
    thumbOutlined: {
      width: 16,
      height: 16,
    },
    trackOutlined: {
      borderRadius: "100px",
      border: `2px solid ${colors.gray500}`,
      backgroundColor: colors.gray100,
      opacity: 1,
    },
    checkedOutlined: {},
    focusVisible: {},
  });

export const AppSwitch = withStyles(AppSwithClasses)(AppSwithComponent);
